import { Ref, computed, ref } from 'vue'
import { ProductFieldsFragment, VariantFieldsFragment } from '_library/src/storefront/generated'

export type ProductSelectionType = {
  hasOptions: boolean
  options: ProductFieldsFragment['options']
  selectedVariant: Ref<VariantFieldsFragment>
  selectedOptions: Ref<VariantFieldsFragment['selectedOptions']>
  selectedAmount: Ref<number>
  quantityMultiplier: Ref<number>
  isPairSelected: Ref<boolean>
  selectVariant: (variant: VariantFieldsFragment) => void
  selectOptionVariant: (variant: VariantFieldsFragment, optionName: string, optionValue: string) => void
}

export const useProductSelection = (product: ProductFieldsFragment) => {
  const variants = product?.variants?.edges.map((edge) => edge.node)
  const firstVariant = variants.find((variant) => variant.availableForSale) || variants[0]

  const pairEnabled = product.availableAsPair?.value === 'true'

  const options = product.options.map((option) => {
    if (
      pairEnabled &&
      option.name === 'QTY' &&
      option.values.includes('Single') &&
      !option.values.includes('Pair')
    ) {
      return {
        ...option,
        values: [...option.values, 'Pair'],
      }
    }

    return option
  })

  const hasOptions = variants.length > 1 || pairEnabled

  const selectedVariant: Ref<VariantFieldsFragment | undefined> = ref(firstVariant)

  const selectedOptions: Ref<VariantFieldsFragment['selectedOptions'] | undefined> = ref(
    selectedVariant.value?.selectedOptions
  )

  const quantityMultiplier = ref(1)

  const isPairSelected = computed(
    () =>
      pairEnabled &&
      selectedOptions.value?.some((option) => option.name === 'QTY' && option.value === 'Pair')
  )

  const selectVariant: ProductSelectionType['selectVariant'] = (variant) => {
    selectedVariant.value = variant
    selectedOptions.value = variant.selectedOptions
    quantityMultiplier.value = 1
  }

  const selectOptionVariant: ProductSelectionType['selectOptionVariant'] = (
    variant,
    optionName,
    optionValue
  ) => {
    const selectingPair = pairEnabled && optionName === 'QTY' && optionValue === 'Pair'

    const preservePair = pairEnabled && isPairSelected.value && optionName !== 'QTY'

    selectedVariant.value = variant

    if (selectingPair || preservePair) {
      selectedOptions.value = variant.selectedOptions.map((option) => {
        if (option.name === 'QTY' && option.value === 'Single') {
          return {
            ...option,
            value: 'Pair',
          }
        }

        return option
      })

      quantityMultiplier.value = 2
      return
    }

    selectedOptions.value = variant.selectedOptions
    quantityMultiplier.value = 1
  }

  const selectedAmount: Ref<number> = computed(() => {
    const amount = selectedVariant.value?.price.amount || 0
    return Number(amount) * quantityMultiplier.value
  })

  return {
    hasOptions,
    selectedVariant,
    selectedOptions,
    options,
    selectVariant,
    selectOptionVariant,
    selectedAmount,
    quantityMultiplier,
    isPairSelected,
  } as ProductSelectionType
}
